/**
 * App mailboxes — read and reply as the addresses an app created through the v1 API
 * (acceso@datosmcp.com, …). Access comes from account membership, not mailbox passwords;
 * every list/read/send is checked and audited server-side (/api/app-mailboxes).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  getAppMailboxAccounts,
  getAppMailboxMessages,
  getAppMailboxMessage,
  sendFromAppMailbox,
} from '../../services/emailApi';

const PAGE_SIZE = 50;

function hostDomain() {
  const parts = window.location.hostname.toLowerCase().split('.');
  return parts.length > 2 ? parts.slice(1).join('.') : parts.join('.');
}

function formatDate(d) {
  if (!d) return '';
  const dt = new Date(d);
  const today = new Date();
  return dt.toDateString() === today.toDateString()
    ? dt.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
    : dt.toLocaleDateString([], { month: 'short', day: 'numeric' });
}

function quote(email) {
  const body = (email.body_text || '').split('\n').map((l) => `> ${l}`).join('\n');
  return `\n\nOn ${new Date(email.created_at).toLocaleString()}, ${email.from_address} wrote:\n${body}`;
}

function ReplyBox({ account, email, onSent }) {
  const defaultFrom = email.email_address_id || account.addresses[0]?.id || '';
  const [fromId, setFromId] = useState(defaultFrom);
  const [to, setTo] = useState(email.from_address || '');
  const [subject, setSubject] = useState(/^re:/i.test(email.subject || '') ? email.subject : `Re: ${email.subject || ''}`);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  async function submit(e) {
    e.preventDefault();
    setSending(true);
    setError('');
    try {
      await sendFromAppMailbox(account.id, { email_address_id: fromId, to, subject, text: text + quote(email) });
      setText('');
      onSent?.();
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.error || 'Send failed');
    } finally {
      setSending(false);
    }
  }

  return (
    <form onSubmit={submit} className="border-t border-gray-200 p-4 space-y-2 bg-gray-50">
      <div className="flex gap-2 text-sm">
        <label className="w-14 pt-1.5 text-gray-500">From</label>
        <select value={fromId} onChange={(e) => setFromId(e.target.value)}
          className="flex-1 rounded-lg border border-gray-300 px-2 py-1.5 text-sm bg-white">
          {account.addresses.map((a) => <option key={a.id} value={a.id}>{a.email_address}</option>)}
        </select>
      </div>
      <div className="flex gap-2 text-sm">
        <label className="w-14 pt-1.5 text-gray-500">To</label>
        <input value={to} onChange={(e) => setTo(e.target.value)} required
          className="flex-1 rounded-lg border border-gray-300 px-2 py-1.5 text-sm" />
      </div>
      <div className="flex gap-2 text-sm">
        <label className="w-14 pt-1.5 text-gray-500">Subject</label>
        <input value={subject} onChange={(e) => setSubject(e.target.value)}
          className="flex-1 rounded-lg border border-gray-300 px-2 py-1.5 text-sm" />
      </div>
      <textarea value={text} onChange={(e) => setText(e.target.value)} rows={5} required placeholder="Write a reply…"
        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20" />
      {error && <div className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">{error}</div>}
      <div className="flex justify-end">
        <button type="submit" disabled={sending || !fromId || !to.trim() || !text.trim()}
          className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-50">
          {sending ? 'Sending…' : 'Send reply'}
        </button>
      </div>
    </form>
  );
}

export default function AppMailboxesView() {
  const [accounts, setAccounts] = useState(null);
  const [accountId, setAccountId] = useState('');
  const [addressId, setAddressId] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [list, setList] = useState({ emails: [], pagination: null });
  const [loadingList, setLoadingList] = useState(false);
  const [selected, setSelected] = useState(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  useEffect(() => {
    getAppMailboxAccounts()
      .then(({ accounts: accs }) => {
        setAccounts(accs);
        const domain = hostDomain();
        const match = accs.find((a) => a.domain?.toLowerCase() === domain)
          || accs.find((a) => a.addresses.some((ad) => ad.domain?.toLowerCase() === domain));
        setAccountId((match || accs[0])?.id || '');
      })
      .catch(() => { setAccounts([]); setError('Could not load app mailboxes.'); });
  }, []);

  useEffect(() => {
    const t = setTimeout(() => { setQ(searchInput.trim()); setPage(1); }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const account = useMemo(() => accounts?.find((a) => a.id === accountId) || null, [accounts, accountId]);

  const loadList = useCallback(() => {
    if (!accountId) return;
    setLoadingList(true);
    getAppMailboxMessages(accountId, { emailAddressId: addressId, q, page, limit: PAGE_SIZE })
      .then(setList)
      .catch(() => setError('Could not load messages.'))
      .finally(() => setLoadingList(false));
  }, [accountId, addressId, q, page]);

  useEffect(() => { loadList(); }, [loadList]);

  async function open(id) {
    setNotice('');
    try {
      const { email } = await getAppMailboxMessage(accountId, id);
      setSelected(email);
    } catch {
      setError('Could not open message.');
    }
  }

  if (accounts === null) {
    return <div className="flex h-full items-center justify-center"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600" /></div>;
  }
  if (accounts.length === 0) {
    return (
      <div className="p-10 text-center text-sm text-gray-500">
        {error || 'You are not a member of any app account yet. Ask an account owner to add you.'}
      </div>
    );
  }

  const addressById = Object.fromEntries((account?.addresses || []).map((a) => [a.id, a.email_address]));
  const pg = list.pagination;

  return (
    <div className="flex h-full flex-col">
      {/* Account switcher + address filter + search */}
      <div className="flex flex-wrap items-center gap-2 border-b border-gray-200 px-3 py-2 bg-white">
        <select value={accountId} onChange={(e) => { setAccountId(e.target.value); setAddressId(''); setPage(1); setSelected(null); }}
          className="rounded-lg border border-gray-300 px-2 py-1 text-[13px] bg-white" title="Account">
          {accounts.map((a) => <option key={a.id} value={a.id}>{a.name}{a.domain ? ` (${a.domain})` : ''}</option>)}
        </select>
        <select value={addressId} onChange={(e) => { setAddressId(e.target.value); setPage(1); setSelected(null); }}
          className="rounded-lg border border-gray-300 px-2 py-1 text-[13px] bg-white" title="Address">
          <option value="">All addresses</option>
          {(account?.addresses || []).map((a) => <option key={a.id} value={a.id}>{a.email_address}</option>)}
        </select>
        <input type="text" placeholder="Search subject or sender" value={searchInput} onChange={(e) => setSearchInput(e.target.value)}
          className="w-56 px-3 py-1 text-[13px] bg-gray-100 border-0 rounded-lg focus:ring-2 focus:ring-blue-500 focus:bg-white placeholder:text-gray-400" />
        <button onClick={loadList} className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded" title="Refresh">
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
        </button>
        <span className="ml-auto text-[11px] text-gray-400">Access is logged</span>
      </div>

      {error && <div className="mx-3 mt-2 rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">{error}</div>}

      <div className="flex flex-1 overflow-hidden">
        {/* Inbox list */}
        <div className={`flex flex-col border-r border-gray-200 ${selected ? 'hidden md:flex md:w-96' : 'flex-1'}`}>
          <div className="flex-1 overflow-y-auto">
            {loadingList && list.emails.length === 0 && <div className="p-6 text-center text-sm text-gray-400">Loading…</div>}
            {!loadingList && list.emails.length === 0 && <div className="p-10 text-center text-sm text-gray-500">No messages</div>}
            {list.emails.map((e) => (
              <button key={e.id} onClick={() => open(e.id)}
                className={`block w-full border-b border-gray-100 px-4 py-2.5 text-left hover:bg-gray-50 ${selected?.id === e.id ? 'bg-blue-50' : ''}`}>
                <div className="flex items-baseline gap-2">
                  <span className={`flex-1 truncate text-sm ${e.is_read ? 'text-gray-700' : 'font-semibold text-gray-900'}`}>{e.from_address}</span>
                  <span className="text-[11px] text-gray-400 flex-shrink-0">{formatDate(e.created_at)}</span>
                </div>
                <div className="truncate text-[13px] text-gray-800">{e.subject || '(no subject)'}</div>
                <div className="flex gap-2 text-[11px] text-gray-400">
                  {!addressId && <span className="truncate">to {addressById[e.email_address_id] || e.to_address}</span>}
                  <span className="truncate">{e.body_preview}</span>
                </div>
              </button>
            ))}
          </div>
          {pg && pg.totalPages > 1 && (
            <div className="flex items-center justify-between border-t border-gray-200 px-3 py-1.5 text-[12px] text-gray-500">
              <button disabled={page <= 1} onClick={() => setPage(page - 1)} className="disabled:opacity-40 hover:text-gray-800">Newer</button>
              <span>Page {pg.page} of {pg.totalPages} · {pg.total}</span>
              <button disabled={page >= pg.totalPages} onClick={() => setPage(page + 1)} className="disabled:opacity-40 hover:text-gray-800">Older</button>
            </div>
          )}
        </div>

        {/* Reader + reply */}
        {selected && (
          <div className="flex flex-1 flex-col min-w-0 overflow-y-auto">
            <div className="flex items-start gap-2 border-b border-gray-200 px-4 py-3">
              <button onClick={() => setSelected(null)} className="rounded p-1 text-gray-400 hover:bg-gray-100" title="Back">
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
              </button>
              <div className="min-w-0 flex-1">
                <h2 className="text-base font-semibold text-gray-900">{selected.subject || '(no subject)'}</h2>
                <p className="text-[12px] text-gray-500 truncate">From {selected.from_address} · to {selected.to_address}{selected.cc_addresses ? ` · cc ${selected.cc_addresses}` : ''}</p>
                <p className="text-[11px] text-gray-400">{new Date(selected.created_at).toLocaleString()}</p>
              </div>
            </div>
            {notice && <div className="mx-4 mt-3 rounded-lg bg-green-50 border border-green-200 px-3 py-2 text-sm text-green-700">{notice}</div>}
            <div className="flex-1 px-4 py-3">
              {selected.body_html
                ? <iframe srcDoc={selected.body_html} sandbox="" title="Email body" className="w-full border-0" style={{ minHeight: '360px' }} />
                : <pre className="whitespace-pre-wrap font-sans text-sm text-gray-800">{selected.body_text}</pre>}
              {Array.isArray(selected.attachments) && selected.attachments.length > 0 && (
                <div className="mt-4 flex flex-wrap gap-2">
                  {selected.attachments.map((a, i) => (
                    <span key={i} className="rounded-lg border border-gray-200 bg-gray-50 px-2 py-1 text-[12px] text-gray-600">
                      {a.filename || a.name || 'attachment'}{a.size ? ` · ${Math.ceil(a.size / 1024)} KB` : ''}
                    </span>
                  ))}
                </div>
              )}
            </div>
            {account && <ReplyBox key={selected.id} account={account} email={selected} onSent={() => setNotice('Reply sent.')} />}
          </div>
        )}
      </div>
    </div>
  );
}
